import type { CampaignReportResponse, CampaignSummary } from "../../types";
import { formatReportDateLabel } from "../../utils/dateFormatting";

export interface OnsiteAwarenessChartDataset {
  label: string;
  data: number[];
  backgroundColor: string;
}

export interface OnsiteAwarenessLayoutViewModel {
  awarenessSummary: {
    totalImpressions: number;
    avgECPM: number;
    avgPDPViewRate: number;
    avgAddToCartRate: number;
    avgCTR: number;
    impressions: number;
    clicks: number;
    pdpViews: number;
    addToCarts: number;
    conversions: number;
    clickRate: number;
    pdpViewRate: number;
    addToCartRate: number;
    conversionRate: number;
  };
  campaignCharts: {
    sortedCampaigns: CampaignSummary[];
    impressionLabels: string[];
    impressionValues: number[];
    ctrLabels: string[];
    ctrValues: number[];
  };
  dailyCharts: {
    labels: string[];
    impressionsDataset: OnsiteAwarenessChartDataset[];
    engagementDatasets: OnsiteAwarenessChartDataset[];
  };
}

const parseNumber = (value: string | number | null | undefined): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const ratePerImpression = (value: number, impressions: number): number =>
  impressions > 0 ? (value / impressions) * 100 : 0;

const buildAwarenessSummary = (
  data: CampaignReportResponse,
): OnsiteAwarenessLayoutViewModel["awarenessSummary"] => {
  const { summary, campaigns } = data;
  const totalImpressions = summary.total_impressions;
  let totalECPMWeighted = 0;
  let totalPDPViewRateWeighted = 0;
  let totalAddToCartRateWeighted = 0;
  let ecpmImpressions = 0;
  let pdpImpressions = 0;
  let atcImpressions = 0;

  campaigns.forEach((campaign) => {
    const campaignImpressions = campaign.total_impressions;

    if (campaign.ecpm) {
      totalECPMWeighted += parseNumber(campaign.ecpm) * campaignImpressions;
      ecpmImpressions += campaignImpressions;
    }
    if (campaign.pdp_view_rate) {
      totalPDPViewRateWeighted +=
        parseNumber(campaign.pdp_view_rate) * campaignImpressions * 100;
      pdpImpressions += campaignImpressions;
    }
    if (campaign.add_to_cart_rate) {
      totalAddToCartRateWeighted +=
        parseNumber(campaign.add_to_cart_rate) * campaignImpressions * 100;
      atcImpressions += campaignImpressions;
    }
  });

  const avgECPM =
    ecpmImpressions > 0
      ? totalECPMWeighted / ecpmImpressions
      : totalImpressions > 0
        ? (parseNumber(summary.total_spend) / totalImpressions) * 1000
        : 0;
  const avgPDPViewRate =
    pdpImpressions > 0 ? totalPDPViewRateWeighted / pdpImpressions : 0.05;
  const avgAddToCartRate =
    atcImpressions > 0 ? totalAddToCartRateWeighted / atcImpressions : 0.03;
  const avgCTR = parseNumber(summary.average_ctr) * 100;
  const impressions = summary.total_impressions;
  const clicks = summary.total_clicks;
  const pdpViews = Math.round(impressions * (avgPDPViewRate / 100));
  const addToCarts = Math.round(impressions * (avgAddToCartRate / 100));
  const conversions = parseNumber(summary.total_units);

  return {
    totalImpressions,
    avgECPM,
    avgPDPViewRate,
    avgAddToCartRate,
    avgCTR,
    impressions,
    clicks,
    pdpViews,
    addToCarts,
    conversions,
    clickRate: ratePerImpression(clicks, impressions),
    pdpViewRate: ratePerImpression(pdpViews, impressions),
    addToCartRate: ratePerImpression(addToCarts, impressions),
    conversionRate: ratePerImpression(conversions, impressions),
  };
};

const buildCampaignCharts = (
  campaigns: CampaignSummary[],
): OnsiteAwarenessLayoutViewModel["campaignCharts"] => {
  const sortedCampaigns = [...campaigns].sort(
    (a, b) => b.total_impressions - a.total_impressions,
  );

  return {
    sortedCampaigns,
    impressionLabels: sortedCampaigns.map((campaign) => campaign.campaign_name),
    impressionValues: sortedCampaigns.map(
      (campaign) => campaign.total_impressions,
    ),
    ctrLabels: sortedCampaigns.map((campaign) => campaign.campaign_name),
    ctrValues: sortedCampaigns.map((campaign) =>
      campaign.total_impressions > 0
        ? (campaign.total_clicks / campaign.total_impressions) * 100
        : 0,
    ),
  };
};

const buildDailyCharts = (
  data: CampaignReportResponse,
): OnsiteAwarenessLayoutViewModel["dailyCharts"] => {
  const labels = data.daily_metrics.map((dailyMetric) =>
    formatReportDateLabel(dailyMetric.report_date),
  );

  return {
    labels,
    impressionsDataset: [
      {
        label: "Impressions",
        data: data.daily_metrics.map(
          (dailyMetric) => dailyMetric.total_impressions,
        ),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
      },
    ],
    engagementDatasets: [
      {
        label: "CTR (%)",
        data: data.daily_metrics.map((dailyMetric) =>
          dailyMetric.total_impressions > 0
            ? (dailyMetric.total_clicks / dailyMetric.total_impressions) * 100
            : 0,
        ),
        backgroundColor: "rgba(34, 197, 94, 0.8)",
      },
    ],
  };
};

export const buildOnsiteAwarenessLayoutViewModel = (
  data: CampaignReportResponse,
): OnsiteAwarenessLayoutViewModel => ({
  awarenessSummary: buildAwarenessSummary(data),
  campaignCharts: buildCampaignCharts(data.campaigns),
  dailyCharts: buildDailyCharts(data),
});
